import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Shield, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Eye } from 'lucide-react';
import { getApiUrl } from '@/config/api';
import { authenticatedFetchWithRetry, createConcurrencyLimiter } from '@/utils/apiUtils';
import { SparklineChart, InfoTooltip } from '@/components/shared';
import { INJURY_RISK_TOOLTIP } from '@/constants/tooltips';
import { TIER_CONFIG, TIER_ORDER, ELEVATED_TIERS } from '@/constants/injuryRisk';
import { InjuryRiskHistoryPoint } from '@/types';

interface TeamInjuryRiskPanelProps {
  athletes: Array<{ id: number; name: string }>;
  isDark: boolean;
  onAthleteClick: (athleteId: number) => void;
  onRiskMapReady?: (map: Map<number, InjuryRiskResponse>) => void;
}

interface SignalContribution {
  signal: string;
  raw_value: number;
  risk_score: number;
  weighted_contribution: number;
  direction: string;
  explanation: string;
}

interface RegionalPainSummary {
  region: string;
  pain_level: number;
  pain_type: string;
  is_new_onset: boolean;
  report_date: string;
}

interface InjuryRiskResponse {
  risk_score: number;
  tier: string;
  confidence: number;
  signal_contributions?: SignalContribution[];
  coach_actions: string[];
  interaction_penalties: string[];
  data_quality: {
    signals_present: number;
    signals_total: number;
    shap_coverage: number;
    subjective_available: boolean;
    confidence_note: string;
  };
  regional_pain_summary?: RegionalPainSummary[];
  risk_narrative?: string;
  date: string;
  status?: string;
}


interface AthleteRisk {
  id: number;
  name: string;
  data: InjuryRiskResponse | null;
  history: InjuryRiskHistoryPoint[] | null;
  error: boolean;
}

const TeamInjuryRiskPanel: React.FC<TeamInjuryRiskPanelProps> = ({
  athletes,
  isDark,
  onAthleteClick,
  onRiskMapReady,
}) => {
  const [riskMap, setRiskMap] = useState<Map<number, InjuryRiskResponse | null>>(new Map());
  const [historyMap, setHistoryMap] = useState<Map<number, InjuryRiskHistoryPoint[]>>(new Map());
  const [initialLoad, setInitialLoad] = useState(true);
  const [errorIds, setErrorIds] = useState<Set<number>>(new Set());
  const [isExpanded, setIsExpanded] = useState(true);
  const [riskFetchCount, setRiskFetchCount] = useState(0); // triggers history re-fetch

  // Refs to access current values in effects without adding them as dependencies
  const riskMapRef = useRef(riskMap);
  riskMapRef.current = riskMap;
  const athletesRef = useRef(athletes);
  athletesRef.current = athletes;

  // Fetch risk scores for all athletes
  useEffect(() => {
    if (!athletes || athletes.length === 0) {
      setInitialLoad(false);
      return;
    }

    let cancelled = false;

    const fetchAll = async () => {
      const results = new Map<number, InjuryRiskResponse | null>();
      const errors = new Set<number>();
      const limit = createConcurrencyLimiter(3);

      await Promise.all(
        athletes.map((athlete) => limit(async () => {
          try {
            const url = getApiUrl(`/api/injury-risk/${athlete.id}/?days=45&include_details=true`);
            const result = await authenticatedFetchWithRetry<InjuryRiskResponse>(
              url, { method: 'GET' }, 'fetching injury risk data', 1
            );
            if (result.success && result.data) {
              const data = result.data;
              if (data.status === 'no_data' || data.status === 'insufficient_data') {
                results.set(athlete.id, null);
              } else if (data.status === 'error') {
                results.set(athlete.id, null);
                errors.add(athlete.id);
              } else {
                results.set(athlete.id, data);
              }
            } else {
              results.set(athlete.id, null);
              errors.add(athlete.id);
            }
          } catch {
            results.set(athlete.id, null);
            errors.add(athlete.id);
          }
        }))
      );

      if (!cancelled) {
        setRiskMap(results);
        setErrorIds(errors);
        setInitialLoad(false);
        setRiskFetchCount(c => c + 1);
        // Notify parent with non-null risk data for badge rendering
        if (onRiskMapReady) {
          const validEntries = new Map<number, InjuryRiskResponse>();
          for (const [id, data] of results) {
            if (data) validEntries.set(id, data);
          }
          onRiskMapReady(validEntries);
        }
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [athletes, onRiskMapReady]);

  // Fetch history for top athletes by risk (after risk scores load).
  // Uses refs for riskMap/athletes to avoid re-triggering on every new Map instance.
  // Depends on riskFetchCount which increments each time risk scores are fetched.
  useEffect(() => {
    if (riskFetchCount === 0) return;

    const currentRiskMap = riskMapRef.current;
    const currentAthletes = athletesRef.current;

    if (currentRiskMap.size === 0) return;

    let cancelled = false;

    // Compute which athletes to fetch history for (mirrors atRiskAthletes logic)
    const withData = currentAthletes
      .filter(a => currentRiskMap.get(a.id) != null)
      .sort((a, b) => (currentRiskMap.get(b.id)?.risk_score ?? 0) - (currentRiskMap.get(a.id)?.risk_score ?? 0));
    const criticalHighElevated = withData.filter(a => {
      const tier = currentRiskMap.get(a.id)?.tier;
      return tier === 'CRITICAL' || tier === 'HIGH' || tier === 'ELEVATED';
    });
    const displayCount = Math.max(criticalHighElevated.length, 5);
    const displayedIds = withData.slice(0, displayCount).map(a => a.id);

    if (displayedIds.length === 0) return;

    const fetchHistory = async () => {
      const results = new Map<number, InjuryRiskHistoryPoint[]>();
      const limit = createConcurrencyLimiter(3);

      await Promise.all(
        displayedIds.map((id) => limit(async () => {
          try {
            const url = getApiUrl(`/api/injury-risk/${id}/history/?days_history=14`);
            const result = await authenticatedFetchWithRetry<{ history: InjuryRiskHistoryPoint[] }>(
              url, { method: 'GET' }, 'fetching injury risk history', 1
            );
            if (result.success && result.data?.history) {
              results.set(id, result.data.history);
            }
          } catch {
            // History fetch failure is non-critical
          }
        }))
      );

      if (!cancelled) {
        // Merge into existing map to prevent sparklines flashing empty during refresh
        setHistoryMap(prev => {
          const merged = new Map(prev);
          for (const [id, history] of results) {
            merged.set(id, history);
          }
          return merged;
        });
      }
    };

    fetchHistory();
    return () => { cancelled = true; };
  }, [riskFetchCount]);

  // Build sorted athlete list
  const sortedAthletes: AthleteRisk[] = useMemo(() => {
    const withData: AthleteRisk[] = [];
    const withoutData: AthleteRisk[] = [];

    for (const athlete of athletes) {
      const data = riskMap.get(athlete.id) ?? null;
      const history = historyMap.get(athlete.id) ?? null;
      const entry = { id: athlete.id, name: athlete.name, data, history, error: errorIds.has(athlete.id) };
      if (data) {
        withData.push(entry);
      } else {
        withoutData.push(entry);
      }
    }

    withData.sort((a, b) => (b.data?.risk_score ?? 0) - (a.data?.risk_score ?? 0));
    return [...withData, ...withoutData];
  }, [athletes, riskMap, errorIds, historyMap]);

  // Show all CRITICAL/HIGH athletes, plus fill to at least 5 with the next highest-risk athletes
  const atRiskAthletes = useMemo(() => {
    const withData = sortedAthletes.filter(a => a.data !== null);
    const criticalHigh = withData.filter(a => a.data!.tier === 'CRITICAL' || a.data!.tier === 'HIGH');
    const elevated = withData.filter(a => a.data!.tier === 'ELEVATED');

    // Always show all CRITICAL + HIGH + ELEVATED
    const mustShow = [...criticalHigh, ...elevated];

    // If fewer than 5, fill with next highest-risk athletes (MODERATE, then LOW)
    if (mustShow.length < 5) {
      const remaining = withData.filter(a =>
        !mustShow.some(m => m.id === a.id)
      );
      const needed = 5 - mustShow.length;
      return [...mustShow, ...remaining.slice(0, needed)];
    }

    return mustShow;
  }, [sortedAthletes]);

  // Tier distribution counts
  const tierCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [, data] of riskMap) {
      if (data?.tier) {
        counts[data.tier] = (counts[data.tier] || 0) + 1;
      }
    }
    return counts;
  }, [riskMap]);

  if (initialLoad && riskMap.size === 0) {
    return (
      <div className="cgm-metric-card rounded-2xl p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Shield className="w-8 h-8 cgm-text-secondary" />
          <h3 className="text-lg font-bold cgm-text-primary">Team Injury Risk</h3>
        </div>
        <div className="flex items-center justify-center h-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-3"></div>
            <p className="cgm-text-secondary text-sm">Calculating team injury risk...</p>
          </div>
        </div>
      </div>
    );
  }

  if (sortedAthletes.length === 0) return null;
  const hasAnyData = sortedAthletes.some(a => a.data !== null);

  return (
    <div className={`cgm-metric-card rounded-2xl p-6 border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}>
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          <Shield className="w-8 h-8" style={{ color: '#ef4444' }} />
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="text-lg font-bold cgm-text-primary">Team Injury Risk</h3>
              <InfoTooltip
                title={INJURY_RISK_TOOLTIP.title}
                body={INJURY_RISK_TOOLTIP.body}
                isDark={isDark}
                iconSize={3.5}
                ariaLabel="About injury risk scoring"
              />
            </div>
            {hasAnyData && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {TIER_ORDER.map((tier) => {
                  const count = tierCounts[tier];
                  if (!count) return null;
                  const config = TIER_CONFIG[tier];
                  return (
                    <span
                      key={tier}
                      className="text-xs font-semibold px-2 py-0.5 rounded whitespace-nowrap"
                      style={{ backgroundColor: `${config.color}20`, color: config.color }}
                    >
                      {count} {config.label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <button className="text-gray-400 hover:text-gray-200 transition-colors p-1">
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </div>

      {/* Content */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[4000px] opacity-100 overflow-visible mt-6' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        {!hasAnyData ? (
          <div className="text-center py-8">
            <p className="cgm-text-secondary text-sm">
              No injury risk data available yet. Athletes need wearable data to generate risk scores.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* At-Risk Athletes Table */}
            {atRiskAthletes.length > 0 && (
              <AtRiskAthletesTable
                athletes={atRiskAthletes}
                isDark={isDark}
                onAthleteClick={onAthleteClick}
              />
            )}

            {/* Team Risk Heatmap */}
            {/* <TeamRiskHeatmap
              athletes={sortedAthletes.filter(a => a.data !== null)}
              isDark={isDark}
              onAthleteClick={onAthleteClick}
            /> */}
          </div>
        )}
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// At-Risk Athletes Table
// ---------------------------------------------------------------------------

interface AtRiskTableProps {
  athletes: AthleteRisk[];
  isDark: boolean;
  onAthleteClick: (id: number) => void;
}

const AtRiskAthletesTable: React.FC<AtRiskTableProps> = ({
  athletes,
  isDark,
  onAthleteClick,
}) => {
  const [expandedAthleteId, setExpandedAthleteId] = useState<number | null>(null);
  return (
    <div>
      <h4 className="text-sm font-semibold cgm-text-primary mb-3">
        Top Athletes by Risk
        <span className="text-xs font-normal cgm-text-secondary ml-2">
          ({athletes.length} athlete{athletes.length !== 1 ? 's' : ''})
        </span>
      </h4>

      {/* Table header */}
      <div className={`grid grid-cols-[1fr_80px_60px] md:grid-cols-[1fr_100px_100px_1fr_80px_minmax(88px,auto)] gap-3 px-3 pb-2 text-xs font-medium cgm-text-secondary border-b ${isDark ? 'border-neutral-700' : 'border-gray-200'}`}>
        <span>Athlete</span>
        <span>Risk Score</span>
        <span className="hidden md:block">Trend (14d)</span>
        <span className="hidden md:block">Top Factor</span>
        <span className="hidden md:block text-center">Days Elevated</span>
        <span className="text-right md:text-center">Actions</span>
      </div>

      {/* Table rows */}
      <div className="divide-y divide-gray-200/30 dark:divide-neutral-700/30">
        {athletes.map((athlete) => {
          const data = athlete.data!;
          const config = TIER_CONFIG[data.tier] || TIER_CONFIG.LOW;
          const riskPercent = Math.round(data.risk_score * 100);
          const initials = getInitials(athlete.name);

          // Sparkline data from history
          const sparklineData = athlete.history?.map(h => h.risk_score * 100) || [];

          // Days elevated: count consecutive days from end of history in ELEVATED+ tiers
          const daysElevated = countDaysElevated(athlete.history);

          // Top factor from signal contributions
          const topContrib = data.signal_contributions
            ?.filter(c => c.direction === 'increasing_risk')
            .sort((a, b) => b.weighted_contribution - a.weighted_contribution)[0];

          // Trend direction (compare first half avg to second half avg)
          const trend = computeTrend(sparklineData);

          return (
            <React.Fragment key={athlete.id}>
              <div
                className={`grid grid-cols-[1fr_80px_60px] md:grid-cols-[1fr_100px_100px_1fr_80px_minmax(88px,auto)] gap-3 items-center px-3 py-3 hover:scale-[1.005] transition-all duration-200 cursor-pointer ${isDark ? 'hover:bg-neutral-800/50' : 'hover:bg-gray-50'}`}
                onClick={() => setExpandedAthleteId(prev => prev === athlete.id ? null : athlete.id)}
              >
                {/* Athlete */}
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: config.color }}
                  >
                    {initials}
                  </div>
                  <span className="text-sm font-semibold cgm-text-primary truncate">{athlete.name}</span>
                </div>

                {/* Risk Score */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="group/provenance relative inline-flex">
                    <span
                      className="text-lg font-bold cursor-help"
                      style={{ color: config.color }}
                    >
                      {riskPercent}
                    </span>
                    <RiskProvenanceTooltip data={data} isDark={isDark} />
                  </span>
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: config.color }}
                  >
                    {config.label}
                  </span>
                  {data.regional_pain_summary && data.regional_pain_summary.length > 0 && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${isDark ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                      {data.regional_pain_summary.length} region{data.regional_pain_summary.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Trend sparkline */}
                <div className="hidden md:flex items-center gap-1">
                  {sparklineData.length > 2 ? (
                    <>
                      <SparklineChart data={sparklineData} color={config.color} width={80} height={28} />
                      {trend !== 0 && (
                        trend > 0
                          ? <TrendingUp className="w-3 h-3 text-red-500" />
                          : <TrendingDown className="w-3 h-3 text-green-500" />
                      )}
                    </>
                  ) : (
                    <span className="text-xs cgm-text-secondary">-</span>
                  )}
                </div>

                {/* Top Factor */}
                <div className="hidden md:block min-w-0">
                  {topContrib ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs cgm-text-secondary">&#8593;</span>
                      <span className="text-xs font-medium cgm-text-primary truncate">
                        {formatTopFactor(topContrib)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs cgm-text-secondary">-</span>
                  )}
                </div>

                {/* Days Elevated */}
                <div className="hidden md:block text-center">
                  <span className="text-sm font-semibold cgm-text-primary">{daysElevated}</span>
                </div>

                {/* Actions */}
                <div
                  className="flex items-center justify-end gap-1.5 flex-wrap"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${isDark ? 'bg-neutral-700 hover:bg-neutral-600 text-neutral-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                    onClick={(e) => { e.stopPropagation(); onAthleteClick(athlete.id); }}
                  >
                    View
                  </button>
                </div>
              </div>

              {/* Expanded narrative panel */}
              {expandedAthleteId === athlete.id && (
                <div className={`px-4 py-3 text-sm border-b ${isDark ? 'bg-neutral-800/30 border-neutral-700/30' : 'bg-gray-50/50 border-gray-200/30'}`}>
                  {data.risk_narrative ? (
                    <p className={`mb-2 ${isDark ? 'text-neutral-300' : 'text-gray-700'}`}>
                      {data.risk_narrative}
                    </p>
                  ) : (
                    <p className={`mb-2 italic ${isDark ? 'text-neutral-500' : 'text-gray-400'}`}>
                      No risk narrative available
                    </p>
                  )}
                  {data.coach_actions && data.coach_actions.length > 0 && (
                    <ul className={`list-disc list-inside space-y-1 text-xs ${isDark ? 'text-neutral-400' : 'text-gray-500'}`}>
                      {data.coach_actions.slice(0, 3).map((action, i) => (
                        <li key={i}>{action}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Risk Provenance Tooltip
// ---------------------------------------------------------------------------

interface RiskProvenanceTooltipProps {
  data: InjuryRiskResponse;
  isDark: boolean;
}

const RISK_DATA_WINDOW_DAYS = 45;

const RiskProvenanceTooltip: React.FC<RiskProvenanceTooltipProps> = ({ data, isDark }) => {
  const signalsPresent = data.data_quality?.signals_present ?? 0;
  const signalsTotal = data.data_quality?.signals_total ?? 0;
  const subjective = data.data_quality?.subjective_available;
  const confidencePct = Math.round((data.confidence ?? 0) * 100);
  const confidenceNote = data.data_quality?.confidence_note;

  const topIncreasing = data.signal_contributions
    ?.filter(c => c.direction === 'increasing_risk')
    .sort((a, b) => b.weighted_contribution - a.weighted_contribution)
    .slice(0, 3)
    .map(c => formatTopFactor(c));

  const scoreDate = (() => {
    try {
      // Anchor bare ISO dates ("YYYY-MM-DD") to local noon so toLocaleDateString
      // doesn't roll back a day for coaches in western timezones (UTC midnight
      // displays as the previous calendar day west of UTC). Matches the
      // convention used in dateUtils.formatDateLabel.
      const d = /^\d{4}-\d{2}-\d{2}$/.test(data.date)
        ? new Date(data.date + 'T12:00:00')
        : new Date(data.date);
      return d.toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
      });
    } catch {
      return data.date;
    }
  })();

  const windowEnd = new Date();
  const windowStart = new Date();
  windowStart.setDate(windowStart.getDate() - RISK_DATA_WINDOW_DAYS);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const windowLabel = `${fmt(windowStart)} – ${fmt(windowEnd)}`;

  return (
    <span
      role="tooltip"
      className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 z-[9999] w-72 p-3 border shadow-lg text-left rounded-md opacity-0 invisible pointer-events-none transition-opacity duration-150 group-hover/provenance:opacity-100 group-hover/provenance:visible group-hover/provenance:pointer-events-auto ${
        isDark ? 'bg-neutral-900 border-neutral-600 text-neutral-200' : 'bg-white border-gray-200 text-gray-900'
      }`}
      style={{ textTransform: 'none', letterSpacing: 'normal', fontWeight: 'normal' }}
    >
      <div className="text-xs font-semibold mb-1.5" style={{ color: isDark ? '#F9FAFB' : '#111827' }}>
        How this score was calculated
      </div>
      <dl className="text-xs space-y-1" style={{ color: isDark ? '#D1D5DB' : '#374151' }}>
        <div className="flex justify-between gap-2">
          <dt className="cgm-text-secondary">Score date</dt>
          <dd className="font-medium text-right">{scoreDate}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="cgm-text-secondary">Data window</dt>
          <dd className="font-medium text-right">{windowLabel}</dd>
        </div>
        {signalsTotal > 0 && (
          <div className="flex justify-between gap-2">
            <dt className="cgm-text-secondary">Wearable signals</dt>
            <dd className="font-medium text-right">{signalsPresent} of {signalsTotal}</dd>
          </div>
        )}
        <div className="flex justify-between gap-2">
          <dt className="cgm-text-secondary">Athlete reports</dt>
          <dd className="font-medium text-right">{subjective ? 'Included' : 'Not included'}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="cgm-text-secondary">Confidence</dt>
          <dd className="font-medium text-right">{confidencePct}%</dd>
        </div>
      </dl>
      {topIncreasing && topIncreasing.length > 0 && (
        <div className="mt-2 pt-2 border-t" style={{ borderColor: isDark ? '#374151' : '#E5E7EB' }}>
          <div className="text-[10px] uppercase tracking-wide cgm-text-secondary mb-1">Top risk drivers</div>
          <ul className="text-xs space-y-0.5" style={{ color: isDark ? '#D1D5DB' : '#374151' }}>
            {topIncreasing.map((factor, i) => (
              <li key={i}>&#8593; {factor}</li>
            ))}
          </ul>
        </div>
      )}
      {confidenceNote && (
        <p className="text-[11px] leading-relaxed mt-2 pt-2 border-t cgm-text-secondary"
           style={{ borderColor: isDark ? '#374151' : '#E5E7EB' }}>
          {confidenceNote}
        </p>
      )}
    </span>
  );
};


// ---------------------------------------------------------------------------
// Team Risk Heatmap
// ---------------------------------------------------------------------------

interface HeatmapProps {
  athletes: AthleteRisk[];
  isDark: boolean;
  onAthleteClick: (id: number) => void;
}

const TeamRiskHeatmap: React.FC<HeatmapProps> = ({ athletes, isDark, onAthleteClick }) => {
  return (
    <div>
      <h4 className="text-sm font-semibold cgm-text-primary mb-3">Team Risk Heatmap</h4>
      <div className="grid grid-cols-5 gap-3">
        {athletes.map((athlete) => {
          const data = athlete.data!;
          const config = TIER_CONFIG[data.tier] || TIER_CONFIG.LOW;
          const riskPercent = Math.round(data.risk_score * 100);
          const initials = getInitials(athlete.name);
          const firstName = athlete.name.split(' ')[0];

          return (
            <div
              key={athlete.id}
              className={`flex flex-col items-center p-3 rounded-xl border-b-[3px] cursor-pointer transition-all duration-200 hover:scale-105 ${isDark ? 'bg-neutral-800/40 border-neutral-700/30' : 'bg-gray-50 border-gray-200'}`}
              style={{ borderBottomColor: config.color }}
              onClick={() => onAthleteClick(athlete.id)}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white mb-1.5"
                style={{ backgroundColor: config.color }}
              >
                {initials}
              </div>
              <span className="text-xs font-medium cgm-text-primary truncate max-w-full">{firstName}</span>
              <span className="text-lg font-bold mt-0.5" style={{ color: config.color }}>
                {riskPercent}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function countDaysElevated(history: InjuryRiskHistoryPoint[] | null): number {
  if (!history || history.length === 0) return 0;
  let count = 0;
  for (let i = history.length - 1; i >= 0; i--) {
    if (ELEVATED_TIERS.has(history[i].tier)) {
      count++;
    } else {
      break;
    }
  }
  return count;
}

function computeTrend(data: number[]): number {
  if (data.length < 4) return 0;
  const mid = Math.floor(data.length / 2);
  const firstHalf = data.slice(0, mid);
  const secondHalf = data.slice(mid);
  const avg1 = firstHalf.reduce((s, v) => s + v, 0) / firstHalf.length;
  const avg2 = secondHalf.reduce((s, v) => s + v, 0) / secondHalf.length;
  const diff = avg2 - avg1;
  if (Math.abs(diff) < 2) return 0; // within noise band
  return diff > 0 ? 1 : -1;
}

const SIGNAL_SHORT_NAMES: Record<string, string> = {
  soreness_3d_max: 'Soreness (3d)',
  soreness: 'Soreness',
  mental_fatigue: 'Mental Fatigue',
  rpe: 'RPE',
  mental_fatigue_slope: 'MF Trend',
  sleep_hours: 'Sleep Deficit',
  sleep_score: 'Sleep Quality',
  rhr: 'RHR',
  hrv_rmssd: 'HRV Cliff',
  hrv_7d_cv: 'HRV Variability',
  hrv_slope: 'HRV Trend',
  rhr_slope: 'RHR Trend',
  acwr_composite: 'ACWR',
  session_load_spike_ratio: 'Load Spike',
  load_change_pct_7d: 'Load Change',
  load_7d_sum: '7d Load',
  consecutive_high_soreness: 'Persistent Soreness',
  rpe_load_divergence: 'RPE Mismatch',
  resp_rate: 'Resp Rate',
  skin_temp: 'Skin Temp',
  load_volatility: 'Load Volatility',
  wearable_missing_rate_7d: 'Data Gap',
  rhr_zscore: 'RHR Z-Score',
  sleep_zscore: 'Sleep Z-Score',
  soreness_slope: 'Soreness Trend',
  acute_chronic_delta: 'Load Gap',
  hrv_drop_x_load_spike: 'HRV x Load',
  region_consecutive_pain: 'Persistent Pain',
  injury_recurrence_risk: 'Recurrence Risk',
};

function formatTopFactor(contrib: SignalContribution): string {
  const name = SIGNAL_SHORT_NAMES[contrib.signal] || contrib.signal;
  const raw = contrib.raw_value;

  // Format raw value contextually
  if (contrib.signal === 'sleep_hours' && raw > 0) return `${name} ${raw.toFixed(1)} hrs`;
  if (contrib.signal === 'hrv_rmssd') return `${name} ${raw > 0 ? raw.toFixed(0) : ''}ms`;
  if (contrib.signal === 'acwr_composite') return `${name} ${raw.toFixed(2)}`;
  if (['soreness', 'soreness_3d_max', 'rpe', 'mental_fatigue'].includes(contrib.signal)) {
    return `${name} ${raw.toFixed(0)}/10`;
  }
  if (contrib.signal.includes('pct') || contrib.signal.includes('change')) {
    return `${name} ${raw > 0 ? '+' : ''}${raw.toFixed(0)}%`;
  }

  return name;
}

export default TeamInjuryRiskPanel;
